import { Container, Spacer, Stack } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { DraftData } from '../api/Drafts';
import { fetchInvitationDraft } from '../api/Forms';
import { formatComposer } from '../data/routes';
import { Authenticated } from './AuthGuards';
import { Fade } from './Fade';
import { FormError, FormSubmissionButton, TextInput } from './Inputs';
import { PageHeader } from './PageHeader';
import { useAuthenticateApiCall } from './hooks/useAuthenticateApiCall';
import { useFormSubmission } from './hooks/useFormSubmission';

const initialValues = {
  name: '',
};

type InviteWithLetterValues = typeof initialValues;
type InviteWithLetterFields = keyof InviteWithLetterValues;

const validationSchema = yup.object().shape<Record<InviteWithLetterFields, any>>({
  name: yup.string().label('Temporary Name').required().max(80),
});

const useOnInviteWithDraft = () => {
  const navigate = useNavigate();

  return useCallback(
    (data: DraftData) => {
      navigate(formatComposer({ letterId: data.id }));
    },
    [navigate]
  );
};

const CreationForm = () => {
  const onInviteWithDraft = useOnInviteWithDraft();
  const { onSubmit } = useFormSubmission(
    useAuthenticateApiCall(fetchInvitationDraft),
    onInviteWithDraft
  );

  return (
    <Formik {...{ initialValues, onSubmit, validationSchema }}>
      <Form>
        <TextInput label="Temporary Name" placeholder="Pal" name="name" type="text" />
        <FormError />
        <Spacer h={4} />
        <FormSubmissionButton type="submit">Start writing</FormSubmissionButton>
      </Form>
    </Formik>
  );
};

const InviteWithLetter = () => (
  <Stack spacing={8}>
    <PageHeader
      emoji={{ symbol: '⛵️', label: 'Emoji of a sailboat' }}
      title={'Invite with a letter'}
      subtitle={
        'Start off your correspondence by drafting a letter and then sharing it with your pal as an invitation. Note: you can change the temporary name at any time prior to sending.'
      }
    />
    <CreationForm />
  </Stack>
);

const AuthInviteWithLetter = () => (
  <Authenticated>
    <Fade>
      <Container w={['xs', 'md', 'lg']}>
        <InviteWithLetter />
      </Container>
    </Fade>
  </Authenticated>
);

export default AuthInviteWithLetter;
